import {FloorPlanItem} from '../types';
import {GRID_MAJOR_SIZE, GRID_SIZE, SNAP_SIZE} from './constants';

export interface CanvasPoint {
    x: number;
    y: number;
}

const positiveModulo = (value: number, divisor: number) => {
    if (divisor === 0) return 0;
    return ((value % divisor) + divisor) % divisor;
};

export const DEFAULT_DIMENSION_STEP = SNAP_SIZE * 2;

export const snapValue = (value: number, unit = SNAP_SIZE): number => {
    const snapped = Math.round(value / unit) * unit;
    return Object.is(snapped, -0) ? 0 : snapped;
};

export const snapPoint = (point: CanvasPoint, unit = SNAP_SIZE): CanvasPoint => ({
    x: snapValue(point.x, unit),
    y: snapValue(point.y, unit),
});

export const offsetPoint = (point: CanvasPoint, offsetX: number, offsetY: number = offsetX): CanvasPoint => ({
    x: point.x + offsetX,
    y: point.y + offsetY,
});

export const normalizeDefaultDimension = (value: number, unit = DEFAULT_DIMENSION_STEP): number =>
    Math.max(unit, snapValue(value, unit));

export const getGridMetrics = (zoomPercent: number, panX: number, panY: number) => {
    const zoomFactor = zoomPercent / 100;
    const minorSize = GRID_SIZE * zoomFactor;
    const majorSize = GRID_MAJOR_SIZE * zoomFactor;

    return {
        minorSize,
        majorSize,
        minorOffsetX: positiveModulo(panX, minorSize),
        minorOffsetY: positiveModulo(panY, minorSize),
        majorOffsetX: positiveModulo(panX, majorSize),
        majorOffsetY: positiveModulo(panY, majorSize),
    };
};

type FabricAnchorTarget = {
    left?: number;
    top?: number;
    angle?: number;
    set: (values: { left: number; top: number }) => void;
    setCoords: () => void;
    floorPlanItem?: FloorPlanItem;
    getCenterPoint?: () => CanvasPoint;
};

export const getObjectAnchorPoint = (obj: FabricAnchorTarget): CanvasPoint => {
    if (typeof obj.getCenterPoint === 'function') {
        const center = obj.getCenterPoint();
        return {x: center.x, y: center.y};
    }

    return {
        x: obj.left ?? 0,
        y: obj.top ?? 0,
    };
};

export const setObjectAnchorPoint = (obj: FabricAnchorTarget, anchor: CanvasPoint) => {
    obj.set({
        left: anchor.x,
        top: anchor.y,
    });
    obj.setCoords();
};

export const syncFloorPlanItemTransform = (
    item: FloorPlanItem,
    left: number,
    top: number,
    angle: number
): FloorPlanItem => {
    if (item.table) {
        return {
            ...item,
            table: {
                ...item.table,
                canvas: {
                    ...item.table.canvas,
                    left,
                    top,
                    angle,
                    scaleX: 1,
                    scaleY: 1,
                },
            },
        };
    }

    if (item.element) {
        return {
            ...item,
            element: {
                ...item.element,
                canvas: {
                    ...item.element.canvas,
                    left,
                    top,
                    angle,
                    scaleX: 1,
                    scaleY: 1,
                },
            },
        };
    }

    return item;
};

export const syncObjectFloorPlanItemTransform = (
    obj: FabricAnchorTarget,
    left: number,
    top: number,
    angle: number = obj.angle ?? 0
) => {
    if (!obj.floorPlanItem) return;
    obj.floorPlanItem = syncFloorPlanItemTransform(obj.floorPlanItem, left, top, angle);
};

export const snapObjectAnchorPoint = (obj: FabricAnchorTarget, unit = SNAP_SIZE): CanvasPoint => {
    const snappedAnchor = snapPoint(getObjectAnchorPoint(obj), unit);
    setObjectAnchorPoint(obj, snappedAnchor);
    syncObjectFloorPlanItemTransform(obj, snappedAnchor.x, snappedAnchor.y, obj.angle ?? 0);
    return snappedAnchor;
};
